import { MutableRefObject, useCallback, useEffect } from 'react';
import {
  useLocation,
  useNavigate,
  useNavigationType,
  useSearchParams,
} from 'react-router-dom';

import { useLoggingMutation, useLoggingQuery } from 'lib/apollo/apollo-hooks';
import {
  INSERT_ACTIVITIES_SECTION,
  UPDATE_ACTIVITIES_SECTION,
  DELETE_ACTIVITIES_SECTION,
  GET_ACTIVITIES_SECTIONS,
} from './lesson-builder.graphql';
import type {
  InsertActivitiesSectionMutation,
  InsertActivitiesSectionMutationVariables,
} from './lesson-builder.graphql.generated';
import type {
  UpdateActivitiesSectionMutation,
  UpdateActivitiesSectionMutationVariables,
} from './lesson-builder.graphql.generated';
import type {
  InsertActivitiesSectionInput,
  UpdateActivitiesSectionInput,
  DeleteActivitiesSectionInput,
} from '../../gql/schema-types';
import type {
  DeleteActivitiesSectionMutation,
  DeleteActivitiesSectionMutationVariables,
} from './lesson-builder.graphql.generated';
import {
  upsertSectionOverview,
  addSectionOverviewToLesson,
  removeSectionOverviewFromLesson,
  modifySubSectionOverviewSort,
  resetInteractiveContentInCache,
  modifyPageTypeInCache,
  modifyActivitiesPublishChanges,
} from './activity-builder.apollo.utils';
import { Differentiation } from 'approot/shared/differentiation/differentiation.constants';
import { useActivityBuilderApolloContext } from './activity-builder.apollo.context';
import {
  ExtraSectionIdentifiers,
  useActivityBuilderApollo,
} from './activity-builder.apollo';
import { WatchQueryFetchPolicy } from '@apollo/client';
import useUserData from 'approot/shared/user/user-data';
import type {
  GetActivitiesSectionsQuery,
  GetActivitiesSectionsQueryVariables,
} from './lesson-builder.graphql.generated';
import {
  ACTIVITIES_PAGE_TYPE_TEACHER,
  ASSESSMENT_ACTIVITIES,
  ActivitiesContentType,
  LANGUAGE_CODE_EN_AU,
} from 'global/constants';
import { getStudentClassToDoClassId } from 'approot/students/logic/get-student-class-to-do-class-id';
import { studentAuthApolloContextOption } from 'approot/student-class-to-do/student-class-to-do.utils';
import { InquisitiveLanguageCodes } from 'global/graphql/scalars.type';
import { useActivityBuilderRequest } from './activity-builder-request.apollo';
import { useGetDraftActivitiesFromCache } from './logic/hooks/use-get-draft-activities-from-cache';
import { useGetInteractiveLessonParams } from './activity-builder.hooks';

type CommonProps = {
  onError?: () => void;
};

type CreateNewSectionProps = {
  contentId: number;
  contentType: ActivitiesContentType;
  onCompleted?: (data: InsertActivitiesSectionMutation) => void;
} & CommonProps;

export const useCreateNewSection = ({
  contentId,
  contentType,
  onCompleted,
  onError,
}: CreateNewSectionProps) => {
  const { userClassId } = useGetInteractiveLessonParams();
  const { createRequest, clearRequest } = useActivityBuilderRequest();

  const interactiveLesson = useGetDraftActivitiesFromCache({
    contentId,
    contentType,
  });
  const { interactiveLessonClone, setInteractiveLessonClone } =
    useActivityBuilderApolloContext();

  const [addSection, { data, loading, error, client }] = useLoggingMutation<
    InsertActivitiesSectionMutation,
    InsertActivitiesSectionMutationVariables
  >(INSERT_ACTIVITIES_SECTION, {
    refetchQueries: [
      'GetLessonTeachingAndLearningSequence',
      'GetAssessmentTeachingAndLearningSequence',
    ],
    onCompleted: result => {
      if (interactiveLessonClone) {
        setInteractiveLessonClone(null);
      }
      onCompleted?.(result);
      // add the activityId to the section overview in the cache
      const { id, activityId } = result.insertActivitiesSection;
      if (id && activityId) {
        client.cache.modify({
          id: client.cache.identify({
            __typename: 'ActivitiesSectionOverview',
            id,
          }),
          fields: {
            activityId: () => activityId,
          },
        });
      }
      modifyActivitiesPublishChanges({
        client,
        id: contentId,
        contentType,
      });
    },
    onError: () => {
      resetInteractiveContentInCache({
        client,
        contentType,
        classId: userClassId,
        interactiveContent: interactiveLessonClone,
      });
      onError?.();
    },
  });

  const createSection = useCallback(
    (input: InsertActivitiesSectionInput) => {
      const requestId = createRequest(input.sectionId);

      if (!interactiveLesson) return;

      // pageType should always be null for assessments
      if (contentType === ASSESSMENT_ACTIVITIES) {
        input.pageType = null;
      }

      setInteractiveLessonClone(interactiveLesson);

      if (input.parentSectionId) {
        const parentSectionOverview = interactiveLesson.sectionsOverview.find(
          s => s.id === input.parentSectionId
        );

        if (parentSectionOverview) {
          const subSectionsOverview =
            parentSectionOverview.subSectionsOverview || [];
          upsertSectionOverview({
            client,
            sectionOverview: {
              ...parentSectionOverview,
              subSectionsOverview: [
                ...subSectionsOverview,
                {
                  __typename: 'ActivitiesSectionOverview',
                  id: input.sectionId,
                  differentiation: parentSectionOverview.differentiation,
                  checkPoint: parentSectionOverview.checkPoint,
                  parentSectionId: input.parentSectionId,
                  sort: input.sort,
                  pageType: input.pageType || ACTIVITIES_PAGE_TYPE_TEACHER,
                  activityId: parentSectionOverview.activityId,
                  hasResponseComponents:
                    parentSectionOverview.hasResponseComponents,
                },
              ],
            },
          });
        }
      } else {
        upsertSectionOverview({
          client,
          sectionOverview: {
            __typename: 'ActivitiesSectionOverview',
            id: input.sectionId,
            sort: input.sort,
            pageType: input.pageType || null,
            differentiation: Differentiation.GreenCircle,
            subSectionsOverview: null,
            checkPoint: false,
            activityId: null,
            hasResponseComponents: false,
          },
        });
        addSectionOverviewToLesson({
          client,
          contentId: interactiveLesson.id,
          sectionId: input.sectionId,
          contentType,
        });
      }

      addSection({
        variables: {
          input,
        },
      })
        .catch(() => {})
        .finally(() => {
          clearRequest(requestId);
        });
    },
    [
      addSection,
      interactiveLesson,
      client,
      setInteractiveLessonClone,
      contentType,
      createRequest,
      clearRequest,
    ]
  );

  return {
    createSection,
    data,
    loading,
    error,
  };
};

type DeleteSectionProps = {
  contentId: number;
  contentType: ActivitiesContentType;
  onCompleted?: (data: DeleteActivitiesSectionMutation) => void;
} & CommonProps;

export const useDeleteSection = ({
  contentId,
  contentType,
  onCompleted,
  onError,
}: DeleteSectionProps) => {
  const { userClassId } = useGetInteractiveLessonParams();
  const { createRequest, clearRequest } = useActivityBuilderRequest();

  const interactiveLesson = useGetDraftActivitiesFromCache({
    contentId,
    contentType,
  });
  const { interactiveLessonClone, setInteractiveLessonClone } =
    useActivityBuilderApolloContext();

  const [doDeleteSection, { data, loading, error, client }] =
    useLoggingMutation<
      DeleteActivitiesSectionMutation,
      DeleteActivitiesSectionMutationVariables
    >(DELETE_ACTIVITIES_SECTION, {
      refetchQueries: [
        'GetLessonTeachingAndLearningSequence',
        'GetAssessmentTeachingAndLearningSequence',
      ],
      onCompleted: result => {
        if (interactiveLessonClone) {
          setInteractiveLessonClone(null);
        }
        onCompleted?.(result);
        modifyActivitiesPublishChanges({
          client,
          id: contentId,
          contentType,
        });
      },
      onError: () => {
        resetInteractiveContentInCache({
          client,
          contentType,
          classId: userClassId,
          interactiveContent: interactiveLessonClone,
        });
        onError?.();
      },
    });

  const deleteSection = useCallback(
    (input: DeleteActivitiesSectionInput, parentSectionId?: string) => {
      const requestId = createRequest(input.sectionId);
      if (!interactiveLesson) return;

      setInteractiveLessonClone(interactiveLesson);

      if (parentSectionId) {
        const sectionOverview = interactiveLesson.sectionsOverview.find(
          s => s.id === parentSectionId
        );
        if (sectionOverview) {
          upsertSectionOverview({
            client,
            sectionOverview: {
              ...sectionOverview,
              subSectionsOverview:
                sectionOverview.subSectionsOverview?.filter(
                  s => s.id !== input.sectionId
                ) || null,
            },
          });
        }
      } else {
        removeSectionOverviewFromLesson({
          client,
          contentId: interactiveLesson.id,
          sectionId: input.sectionId,
          contentType,
        });
      }

      doDeleteSection({
        variables: {
          input,
        },
      })
        .catch(() => {})
        .finally(() => {
          clearRequest(requestId);
        });
    },
    [
      doDeleteSection,
      interactiveLesson,
      client,
      setInteractiveLessonClone,
      contentType,
      createRequest,
      clearRequest,
    ]
  );

  return {
    deleteSection,
    data,
    loading,
    error,
  };
};

type UpdateSectionProps = {
  contentId: number;
  contentType: ActivitiesContentType;
  onCompleted?: (data: UpdateActivitiesSectionMutation) => void;
} & CommonProps;

export const useUpdateSection = ({
  contentId,
  contentType,
  onCompleted,
  onError,
}: UpdateSectionProps) => {
  const { userClassId } = useGetInteractiveLessonParams();
  const { createRequest, clearRequest } = useActivityBuilderRequest();

  const interactiveLesson = useGetDraftActivitiesFromCache({
    contentId,
    contentType,
  });
  const { interactiveLessonClone, setInteractiveLessonClone } =
    useActivityBuilderApolloContext();

  const [addSection, { data, loading, error, client }] = useLoggingMutation<
    UpdateActivitiesSectionMutation,
    UpdateActivitiesSectionMutationVariables
  >(UPDATE_ACTIVITIES_SECTION, {
    onCompleted: result => {
      if (interactiveLessonClone) {
        setInteractiveLessonClone(null);
      }
      onCompleted?.(result);
      modifyActivitiesPublishChanges({
        client,
        id: contentId,
        contentType,
      });
    },
    onError: () => {
      resetInteractiveContentInCache({
        client,
        contentType,
        classId: userClassId,
        interactiveContent: interactiveLessonClone,
      });
      onError?.();
    },
  });

  const updateSection = useCallback(
    (input: UpdateActivitiesSectionInput) => {
      const requestId = createRequest(input.sectionId);

      if (!interactiveLesson) return;

      // pageType should always be null for assessments
      if (contentType === ASSESSMENT_ACTIVITIES) {
        input.pageType = null;
      }

      setInteractiveLessonClone(interactiveLesson);

      const sectionsOverview = interactiveLesson?.sectionsOverview || [];
      if (input.parentSectionId) {
        // updating a sub-section
        if (input.sectionId) {
          modifySubSectionOverviewSort({
            client,
            id: input.sectionId,
            sort: input.sort,
          });
        }
      } else {
        const sectionOverviewToUpdate = sectionsOverview.find(
          s => s.id === input.sectionId
        );

        if (sectionOverviewToUpdate) {
          upsertSectionOverview({
            client,
            sectionOverview: {
              ...sectionOverviewToUpdate,
              sort: input.sort,
              pageType: input.pageType || null,
            },
          });
        }

        if (input.pageType) {
          modifyPageTypeInCache({
            client,
            id: input.sectionId,
            pageType: input.pageType,
          });
        }
      }

      addSection({
        variables: {
          input,
        },
      })
        .catch(() => {})
        .finally(() => {
          clearRequest(requestId);
        });
    },
    [
      addSection,
      interactiveLesson,
      client,
      setInteractiveLessonClone,
      contentType,
      clearRequest,
      createRequest,
    ]
  );

  return {
    updateSection,
    data,
    loading,
    error,
  };
};

type GetInteractiveSectionsProps = {
  contentId: number;
  contentType: ActivitiesContentType;
  classId?: number | null;
  sectionIds?: string[];
  fetchPolicy?: WatchQueryFetchPolicy;
  skip?: boolean;
  version?: string | null;
  language?: InquisitiveLanguageCodes;
  isPublic?: boolean;
  onCompleted?: (res: GetActivitiesSectionsQuery) => void;
};

export const useGetActivitySections = ({
  contentId,
  contentType,
  classId,
  sectionIds,
  fetchPolicy = 'cache-first',
  skip,
  version,
  language = LANGUAGE_CODE_EN_AU,
  isPublic,
  onCompleted,
}: GetInteractiveSectionsProps) => {
  const { isSignedIn, isStudentSignedIn } = useUserData();

  const resolvedClassId = isStudentSignedIn
    ? (getStudentClassToDoClassId() ?? classId)
    : classId || null;

  const { data, loading, error } = useLoggingQuery<
    GetActivitiesSectionsQuery,
    GetActivitiesSectionsQueryVariables
  >(
    GET_ACTIVITIES_SECTIONS,
    {
      context: isStudentSignedIn ? studentAuthApolloContextOption() : undefined,
      skip:
        (!resolvedClassId && (isSignedIn || isStudentSignedIn)) ||
        !contentId ||
        skip,
      fetchPolicy,
      variables: {
        input: {
          contentId,
          contentType,
          sectionsIds: sectionIds,
          version,
          language,
          public: isPublic,
          // we only need this for student users to get access
          // to signed URLs
          classId: resolvedClassId,
        },
      },
      onCompleted: res => onCompleted?.(res),
    },
    isSignedIn
  );

  return {
    data,
    loading,
    error,
  };
};

export const useUpdateSectionUsingParams = ({
  sortedSections,
  isInitialisedRef,
}: {
  sortedSections: { id: string }[];
  isInitialisedRef: MutableRefObject<boolean>;
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const navigationType = useNavigationType();
  const [searchParams] = useSearchParams();
  const section = searchParams.get('page');
  const version = searchParams.get('version');

  const { setActiveSection } = useActivityBuilderApollo();

  useEffect(() => {
    if (
      section === ExtraSectionIdentifiers.Introduction ||
      section === ExtraSectionIdentifiers.Conclusion
    ) {
      setActiveSection(section);
      return;
    }

    // initialise to a particular page on load
    if (sortedSections.length > 0 && !isInitialisedRef.current) {
      if (section) {
        if (sortedSections.find(s => s.id === section)) {
          setActiveSection(section);
        } else {
          // invalid section id
          setActiveSection(ExtraSectionIdentifiers.Introduction);
          const urlWithoutParams = location.pathname;
          if (version) {
            navigate(
              `${urlWithoutParams}?page=${ExtraSectionIdentifiers.Introduction}&version=${version}`
            );
          } else {
            navigate(
              `${urlWithoutParams}?page=${ExtraSectionIdentifiers.Introduction}`
            );
          }
        }
      } else {
        setActiveSection(ExtraSectionIdentifiers.Introduction);
      }
      isInitialisedRef.current = true;
    }
    // eslint-disable-next-line
  }, [sortedSections, section]);

  useEffect(() => {
    // listen for any changes to params (e.g. click browser back button)
    if (navigationType === 'POP') {
      if (section === ExtraSectionIdentifiers.Conclusion) {
        setActiveSection(section);
        return;
      }

      if (section !== ExtraSectionIdentifiers.Introduction) {
        if (section && sortedSections.length > 0) {
          if (sortedSections.find(s => s.id === section)) {
            setActiveSection(section);
          } else {
            // invalid section id
            const urlWithoutParams = location.pathname;
            if (version) {
              navigate(`${urlWithoutParams}?version=${version}`);
            } else {
              navigate(urlWithoutParams);
            }
            setActiveSection(ExtraSectionIdentifiers.Introduction);
          }
        } else {
          setActiveSection(ExtraSectionIdentifiers.Introduction);
        }
      }
    }
    // eslint-disable-next-line
  }, [navigationType, navigate, section]);
};
